import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Room {
  id: string
  name: string
  room_type: string
  building: string
  floor: number
  capacity: number
  amenities: string[]
  is_available: boolean
}

interface Reservation {
  id: string
  reservation_id: string
  room_name: string
  title: string
  meeting_type: string
  organizer: string
  attendees_count: number
  start_time: string
  end_time: string
  status: string
}

export default function RoomsPage() {
  const [rooms, setRooms] = useState<Room[]>([])
  const [reservations, setReservations] = useState<Reservation[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [roomRes, resRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/rooms/'),
      fetch(API_BASE + '/rooms/reservations'),
      fetch(API_BASE + '/rooms/statistics')
    ])
    if (roomRes.ok) setRooms((await roomRes.json()).rooms || [])
    if (resRes.ok) setReservations((await resRes.json()).reservations || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': case 'completed': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-blue-100 text-blue-800'
      case 'cancelled': case 'no_show': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Room Reservations</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Book Room</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Rooms</div>
              <div className="text-2xl font-bold">{statistics.total_rooms}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Available Now</div>
              <div className="text-2xl font-bold text-green-600">{statistics.available_rooms}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Today's Bookings</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.total_reservations_today}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Utilization</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.utilization_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending Approvals</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.pending_approvals}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Meeting Rooms</h2></div>
            <div className="p-6 grid grid-cols-3 gap-4">
              {rooms.slice(0, 12).map(room => (
                <div key={room.id} className={`p-4 rounded-lg border-2 ${room.is_available ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="font-medium">{room.name}</div>
                    <span className={`px-2 py-0.5 rounded text-xs ${room.is_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {room.is_available ? 'Available' : 'In Use'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 capitalize mb-2">{room.room_type} - {room.building} Floor {room.floor}</div>
                  <div className="text-sm text-gray-600 mb-2">{room.capacity} people</div>
                  <div className="flex flex-wrap gap-1">
                    {room.amenities.slice(0, 3).map((amenity, i) => (
                      <span key={i} className="text-xs bg-blue-100 text-blue-800 px-1 rounded">{amenity.replace(/_/g, ' ')}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Upcoming Reservations</h3>
            <div className="space-y-3">
              {reservations.filter(r => r.status === 'confirmed').slice(0, 10).map(res => (
                <div key={res.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm truncate flex-1">{res.title}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ml-2 ${getStatusColor(res.status)}`}>{res.status}</span>
                  </div>
                  <div className="text-xs text-gray-500">{res.room_name} - {res.organizer}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-600">{res.attendees_count} attendees</span>
                    <span className="text-xs text-gray-500">{new Date(res.start_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
